import { createSlice } from "@reduxjs/toolkit";

import {
  getGroups,
  getUsers,
  getRoles,
  getRobots,
  updateUsage,
  editRoleResource,
  newGroup,
  editGroupResource,
  newTier,
} from "../../services/api/admin";

const adminSlice = createSlice({
  name: "admin",
  initialState: {
    groups: null,
    roles: null,
    users: null,
    robots: null,
  },

  reducers: {
    fetchGroupsListRequest: (state) => ({
      ...state,
      fetchingGroupsList: true,
      fetchingGroupsListError: null,
    }),

    fetchGroupsListFailure: (state, action) => ({
      ...state,
      fetchingGroupsList: false,
      fetchingGroupsListError: action.payload,
    }),

    fetchGroupsListSuccess: (state, action) => ({
      ...state,
      fetchingGroupsList: false,
      fetchingGroupsListError: null,
      groups: action.payload.data,
    }),

    createGroupRequest: (state) => ({
      ...state,
      creatingGroup: true,
      creatingGroupError: null,
    }),
    createGroupFailure: (state, action) => ({
      ...state,
      creatingGroup: false,
      creatingGroupError: action.payload,
    }),
    createGroupSuccess: (state, action) => ({
      ...state,
      creatingGroup: false,
      creatingGroupError: null,
      groups: [action.payload, ...state.groups],
    }),

    updateGroupResourceRequest: (state) => ({
      ...state,
      updatingGroupResource: true,
      updatingGroupResourceError: null,
    }),
    updateGroupResourceFailure: (state, action) => ({
      ...state,
      updatingGroupResource: false,
      updatingGroupResourceError: action.payload,
    }),
    updateGroupResourceSuccess: (state, action) => ({
      ...state,
      updatingGroupResource: false,
      updatingGroupResourceError: null,
      groups: [...state.groups].map(
        (obj) => [action.payload.data].find((o) => o.id === obj.id) || obj
      ),
    }),

    createRobotTierRequest: (state) => ({
      ...state,
      creatingRobotTier: true,
      creatingRobotTierError: null,
    }),
    createRobotTierFailure: (state, action) => ({
      ...state,
      creatingRobotTier: false,
      creatingRobotTierError: action.payload,
    }),
    createRobotTierSuccess: (state, action) => ({
      ...state,
      creatingRobotTier: false,
      creatingRobotTierError: null,
      robots: state.robots.map((item, idx) =>
        item.id === action.payload.data.robot_id
          ? { ...item, tier: { ...action.payload.data } }
          : item
      ),
    }),

    updateRobotTierRequest: (state) => ({
      ...state,
      updatingRobotTier: true,
      updatingRobotTierError: null,
    }),
    updateRobotTierFailure: (state, action) => ({
      ...state,
      updatingRobotTier: false,
      updatingRobotTierError: action.payload,
    }),
    updateRobotTierSuccess: (state, action) => ({
      ...state,
      updatingRobotTier: false,
      updatingRobotTierError: null,
      robots: state.robots.map((item, idx) =>
        item.id === action.payload.data.robot_id
          ? { ...item, tier: { ...action.payload.data } }
          : item
      ),
    }),

    fetchUsersListRequest: (state) => ({
      ...state,
      fetchingUsersList: true,
      fetchingUsersListError: null,
    }),

    fetchUsersListFailure: (state, action) => ({
      ...state,
      fetchingUsersList: false,
      fetchingUsersListError: action.payload,
    }),

    fetchUsersListSuccess: (state, action) => ({
      ...state,
      fetchingUsersList: false,
      fetchingUsersListError: null,
      users: action.payload.data,
    }),

    fetchRolesListRequest: (state) => ({
      ...state,
      fetchingRolesList: true,
      fetchingRolesListError: null,
    }),

    fetchRolesListFailure: (state, action) => ({
      ...state,
      fetchingRolesList: false,
      fetchingRolesListError: action.payload,
    }),

    fetchRolesListSuccess: (state, action) => ({
      ...state,
      fetchingRolesList: false,
      fetchingRolesListError: null,
      roles: action.payload.data,
    }),

    updateRoleResourceRequest: (state) => ({
      ...state,
      updatingRoleResource: true,
      updatingRoleResourceError: null,
    }),
    updateRoleResourceFailure: (state, action) => ({
      ...state,
      updatingRoleResource: false,
      updatingRoleResourceError: action.payload,
    }),
    updateRoleResourceSuccess: (state, action) => ({
      ...state,
      updatingRoleResource: false,
      updatingRoleResourceError: null,
      users: [...state.users].map((obj) => ({
        ...obj,
        ...[action.payload.data].find((o) => o.id === obj.id),
      })),
    }),

    fetchRobotsListRequest: (state) => ({
      ...state,
      fetchingRobotsList: true,
      fetchingRobotsListError: null,
    }),

    fetchRobotsListFailure: (state, action) => ({
      ...state,
      fetchingRobotsList: false,
      fetchingRobotsListError: action.payload,
    }),

    fetchRobotsListSuccess: (state, action) => ({
      ...state,
      fetchingRobotsList: false,
      fetchingRobotsListError: null,
      robots: action.payload.data,
    }),
  },
});

export const {
  fetchGroupsListRequest,
  fetchGroupsListFailure,
  fetchGroupsListSuccess,

  createGroupRequest,
  createGroupSuccess,
  createGroupFailure,

  updateGroupResourceRequest,
  updateGroupResourceSuccess,
  updateGroupResourceFailure,

  createRobotTierRequest,
  createRobotTierFailure,
  createRobotTierSuccess,

  updateRobotTierRequest,
  updateRobotTierFailure,
  updateRobotTierSuccess,

  fetchUsersListRequest,
  fetchUsersListFailure,
  fetchUsersListSuccess,

  fetchRolesListRequest,
  fetchRolesListFailure,
  fetchRolesListSuccess,

  updateRoleResourceRequest,
  updateRoleResourceSuccess,
  updateRoleResourceFailure,

  fetchRobotsListRequest,
  fetchRobotsListFailure,
  fetchRobotsListSuccess,
} = adminSlice.actions;

export default adminSlice.reducer;

export const getGroupsList = () => async (dispatch) => {
  try {
    dispatch(fetchGroupsListRequest());

    const { data } = await getGroups();

    dispatch(fetchGroupsListSuccess({ data }));
  } catch (err) {
    dispatch(fetchGroupsListFailure(err.toString()));
  }
};

export const createGroup = (payload) => async (dispatch) => {
  try {
    dispatch(createGroupRequest());

    const { data } = await newGroup(payload);

    dispatch(createGroupSuccess({ ...data, users: null, robots: null }));
    getGroupsList();
  } catch (err) {
    dispatch(createGroupFailure(err.toString()));
  }
};

export const updateGroupResource = (id, payload) => async (dispatch) => {
  try {
    dispatch(updateGroupResourceRequest());

    const { data } = await editGroupResource(id, payload);

    dispatch(updateGroupResourceSuccess({ data }));
  } catch (err) {
    dispatch(updateGroupResourceFailure(err.toString()));
  }
};

export const getUsersList = () => async (dispatch) => {
  try {
    dispatch(fetchUsersListRequest());

    const { data } = await getUsers();

    dispatch(fetchUsersListSuccess({ data }));
  } catch (err) {
    dispatch(fetchUsersListFailure(err.toString()));
  }
};

export const getRolesList = () => async (dispatch) => {
  try {
    dispatch(fetchRolesListRequest());

    const { data } = await getRoles();

    dispatch(fetchRolesListSuccess({ data }));
  } catch (err) {
    dispatch(fetchRolesListFailure(err.toString()));
  }
};

export const updateRoleResource = (id, payload) => async (dispatch) => {
  try {
    dispatch(updateRoleResourceRequest());

    const { data } = await editRoleResource(id, payload);

    dispatch(
      updateRoleResourceSuccess({
        data: { id: id, role: getRoleById(payload.role_id) },
      })
    );
  } catch (err) {
    dispatch(updateRoleResourceFailure(err.toString()));
  }
};

export const getRobotsList = () => async (dispatch) => {
  try {
    dispatch(fetchRobotsListRequest());

    const data = await getRobots();

    dispatch(fetchRobotsListSuccess({ data }));
  } catch (err) {
    dispatch(fetchRobotsListFailure(err.toString()));
  }
};

export const createRobotTier = (id) => async (dispatch) => {
  try {
    dispatch(createRobotTierRequest());

    const { data } = await newTier(id);

    dispatch(createRobotTierSuccess({ data }));
  } catch (err) {
    dispatch(createRobotTierFailure(err.toString()));
  }
};

export const updateRobotTier = (id, payload) => async (dispatch) => {
  try {
    dispatch(updateRobotTierRequest(id, payload));

    const { data } = await updateUsage(id, payload);

    // const payload = {
    //   tier: {}, // goes to // put users/robots/robot_id/bandwidth
    //   // tier, capacity only

    //   groups, // goes to group management
    //   customer, location // goes to other
    // };

    dispatch(updateRobotTierSuccess({ data }));
  } catch (err) {
    dispatch(updateRobotTierFailure(err.toString()));
  }
};

function getRoleById(id) {
  switch (id) {
    case 1:
      return "admin";
    case 2:
      return "support";
    case 3:
      return "customer";
    case 4:
      return "demo";
    case 5:
      return "analyzer";
    case 6:
      return "supervisor";
    default:
      return;
  }
}
